import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, makeStyles, Chip, CircularProgress, FormControl, Select, MenuItem } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import { useHistory, useParams } from "react-router-dom";
import ComplaintActionForm from "./form/ComplaintActionForm";
import AppDialog from "components/Dialog/AppDialog";
import PreviewDialog from "components/Dialog/PreviewDialog";
import {
  fetchComplaintDetail,
  fetchComplaintEvidence,
  uploadComplaintEvidence,
  deleteComplaintEvidence,
  getComplaintDetail,
  getComplaintEvidenceList,
  updateComplaintStatus,
} from "store/slices/complaint";
import { fetchPageAction, createAction, updateActionStatus, getActionList } from "store/slices/action";
import { fetchCompanyUserListPaging, getCompanyUserListPaging } from "store/slices/companyUser";
import { fetchWorkerListPaging, getWorkerList } from "store/slices/worker";
import { getAuthInfo } from "store/slices/auth";
import { COMPLAINT_STATUS, COMPLAINT_SUBMITTER_TYPE, ACTION_SOURCE, ACTION_OWNER, ACTION_STATUS } from "config/AppConfig";

const successColor = '#1D9E75';
const useStyles = makeStyles(() => ({
  container: {
    minHeight: "100vh",
    padding: "10px",
    background: "#fff",
  },
  breadcrumb: {
    padding: "16px 24px",
    borderBottom: "0.5px solid #e5e5e2",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  breadcrumbLink: {
    fontSize: "13px",
    color: successColor,
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
  },
  slaBanner: {
    background: "#FCEBEB",
    borderBottom: "0.5px solid #F09595",
    padding: "10px 24px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  headerRow: {
    padding: "24px 24px 0 24px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "20px",
  },
  metaRef: {
    fontSize: "11px",
    fontWeight: 500,
    color: "#888780",
    letterSpacing: "0.04em",
    marginBottom: "4px",
  },
  headerTitle: {
    fontSize: "20px",
    fontWeight: 500,
    marginBottom: "8px",
  },
  metaInfoRow: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    flexWrap: "wrap",
  },
  metaInfoItem: {
    fontSize: "13px",
    color: "#5f5e5a",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  card: {
    background: "#fff",
    borderRadius: "12px",
    border: "0.5px solid #e5e5e2",
    padding: "20px",
    marginBottom: "16px",
  },
  cardTitle: {
    fontSize: "14px",
    fontWeight: 500,
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  stepsContainer: {
    display: "flex",
    alignItems: "flex-start",
  },
  stepItem: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
  },
  stepCircle: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 500,
    zIndex: 1,
    position: "relative",
  },
  stepLine: {
    height: "2px",
    width: "100%",
    position: "absolute",
    top: "16px",
    left: "50%",
    zIndex: 0,
  },
  stepLabel: {
    fontSize: "11px",
    fontWeight: 500,
    marginTop: "6px",
    textAlign: "center",
  },
  stepHint: {
    marginTop: "12px",
    paddingTop: "12px",
    borderTop: "0.5px solid #e5e5e2",
    fontSize: "12px",
    color: "#888780",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px 24px",
  },
  infoItemFull: {
    gridColumn: "1 / -1",
  },
  infoLabel: {
    fontSize: "11px",
    fontWeight: 500,
    color: "#888780",
    letterSpacing: "0.04em",
    marginBottom: "4px",
    textTransform: "uppercase",
  },
  infoValue: {
    fontSize: "13px",
    color: "#1a1a18",
  },
  infoValueBox: {
    background: "#F5F5F3",
    border: "0.5px solid #e5e5e2",
    borderRadius: "8px",
    padding: "10px 12px",
    lineHeight: "1.6",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "8px 0",
    fontSize: "11px",
    fontWeight: 500,
    color: "#888780",
    borderBottom: "0.5px solid #e5e5e2",
  },
  td: {
    padding: "10px 0",
    borderBottom: "0.5px solid #F1EFE8",
  },
  uploadZone: {
    border: "1.5px dashed #c5c4bc",
    borderRadius: "8px",
    padding: "24px",
    textAlign: "center",
  },
  footerButtons: {
    padding: "0 24px",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
  },
}));

const ComplaintDetail = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const editActionFileInputRef = useRef(null);

  const complaint = useSelector(getComplaintDetail);
  const evidenceList = useSelector(getComplaintEvidenceList);
  const actions = useSelector(getActionList);
  const companyUsers = useSelector(getCompanyUserListPaging);
  const workers = useSelector(getWorkerList);
  const authUser = useSelector(getAuthInfo);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);

  // Status Change Confirm Dialog State
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmStepIndex, setConfirmStepIndex] = useState(null);
  const [selectedOutcome, setSelectedOutcome] = useState("UPHELD");

  // Edit Action Status Dialog State
  const [openEditActionDialog, setOpenEditActionDialog] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [selectedActionStatus, setSelectedActionStatus] = useState("");
  const [selectedActionDueDate, setSelectedActionDueDate] = useState("");
  const [editActionFile, setEditActionFile] = useState(null);

  // Delete Evidence Confirm Dialog State
  const [openDeleteEvidenceDialog, setOpenDeleteEvidenceDialog] = useState(false);
  const [deleteEvidenceTarget, setDeleteEvidenceTarget] = useState(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchComplaintDetail({ id }));
      dispatch(fetchComplaintEvidence({ id }));
      dispatch(fetchPageAction({ source: ACTION_SOURCE.COMPLAINT, complaintId: id, page: 1, size: 50 }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (authUser && authUser.companyId) {
      dispatch(fetchCompanyUserListPaging({ company_id: authUser.companyId, page_number: 1, page_size: 1000 }));
      dispatch(fetchWorkerListPaging({ companyId: authUser.companyId, page: 1, size: 1000 }));
    }
  }, [dispatch, authUser]);

  const getRefLabel = () => {
    if (complaint.complaintNumber) return complaint.complaintNumber;
    return complaint.id ? `CMP-${String(complaint.id).padStart(2, "0")}` : "";
  };

  const getSubmitterLabel = (submitterType) => {
    switch (submitterType) {
      case COMPLAINT_SUBMITTER_TYPE.PARTICIPANT:
        return t('label.participant');
      case COMPLAINT_SUBMITTER_TYPE.FAMILY_GUARDIAN:
        return t('label.complaint.family');
      case COMPLAINT_SUBMITTER_TYPE.WORKER:
        return t('label.complaint.worker');
      default:
        return t('label.other');
    }
  };

  const getAssignedToName = (assignedToUserId) => {
    if (!assignedToUserId) return "";
    const user = companyUsers.find((u) => u.id === assignedToUserId);
    if (user) return `${user.firstName || ""} ${user.lastName || ""}`.trim();
    const worker = workers.find((w) => w.id === assignedToUserId);
    if (worker) return worker.name || `${worker.firstName || ""} ${worker.lastName || ""}`.trim();
    return `User #${assignedToUserId}`;
  };

  const companyUserOptions = (companyUsers || []).map((u) => ({
    value: u.id,
    label: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
  }));

  const workerOptions = (workers || []).map((w) => ({
    value: w.id,
    label: w.name || `${w.firstName || ""} ${w.lastName || ""}`.trim(),
  }));

  const getActionAssignedName = (action) => {
    if (action.assignedToName) return action.assignedToName;
    if (action.owner === ACTION_OWNER.WORKER && action.assignedToUserId) {
      const worker = (workers || []).find((w) => w.id === action.assignedToUserId);
      if (worker) return worker.name || `${worker.firstName || ""} ${worker.lastName || ""}`.trim();
    }
    return getOwnerLabel(action.owner);
  };

  const getRelationshipLabel = (relationship) => {
    switch (relationship) {
      case "SUPPORT_WORKER":
        return t('label.complaint.relationship_support_worker');
      case "TEAM_LEADER_COORDINATOR":
        return t('label.complaint.relationship_team_leader_coordinator');
      case "ANOTHER_PARTICIPANT":
        return t('label.complaint.relationship_another_participant');
      case "ANOTHER_WORKER":
        return t('label.complaint.relationship_another_worker');
      case "ORGANISATION_SERVICE_GENERALLY":
        return t('label.complaint.relationship_organisation_service_generally');
      default:
        return relationship || "";
    }
  };

  const getSteps = () => {
    const status = complaint.status;
    const steps = [
      { label: t('label.complaint.step_received'), done: true, statusKey: "RECEIVED" },
      { label: t('label.complaint.under_review'), active: false, statusKey: "UNDER_REVIEW" },
      { label: t('label.complaint.step_outcome_decided'), pending: true, statusKey: "OUT_COME_DECIDED" },
      { label: t('label.complaint.step_closed'), pending: true, statusKey: "CLOSED" },
    ];

    if (status === "UNDER_REVIEW") {
      steps[1].active = true;
    } else if (status === "OUT_COME_DECIDED") {
      steps[1].done = true;
      steps[2].active = true;
    } else if (status === "CLOSED") {
      steps[1].done = true;
      steps[2].done = true;
      steps[3].done = true;
    }

    return steps;
  };

  const steps = getSteps();

  const getNextStepIndex = () => {
    const status = complaint.status;
    if (status === "RECEIVED") return 1;
    if (status === "UNDER_REVIEW") return 2;
    if (status === "OUT_COME_DECIDED") return 3;
    return -1;
  };

  const nextStepIndex = getNextStepIndex();

  const handleStepClick = (index) => {
    if (index !== nextStepIndex) return;
    setConfirmStepIndex(index);
    if (index === 2) {
      setSelectedOutcome("UPHELD");
    }
    setOpenConfirmDialog(true);
  };

  const handleConfirmStatusChange = () => {
    const targetStatus = steps[confirmStepIndex]?.statusKey;
    if (!targetStatus) return;

    const params = {
      id: complaint.id,
      status: targetStatus,
    };

    if (targetStatus === "OUT_COME_DECIDED") {
      params.optionOutcomeDecided = selectedOutcome;
    }

    dispatch(updateComplaintStatus(params)).then((res) => {
      if (res.meta.requestStatus === "fulfilled" || !res.error) {
        dispatch(fetchComplaintDetail({ id }));
      }
    });

    setOpenConfirmDialog(false);
  };

  const handleAssigneeChange = (userId) => {
    const params = {
      id: complaint.id,
      status: complaint.status,
      assignedToUserId: userId || null,
    };
    dispatch(updateComplaintStatus(params)).then((res) => {
      if (res.meta.requestStatus === "fulfilled" || !res.error) {
        dispatch(fetchComplaintDetail({ id }));
      }
    });
  };

  const getCurrentStatusLabel = () => {
    const status = complaint.status;
    if (status === "RECEIVED") return t('label.complaint.step_received') || "Received";
    if (status === "UNDER_REVIEW") return t('label.complaint.under_review') || "Under review";
    if (status === "OUT_COME_DECIDED") return t('label.complaint.step_outcome_decided') || "Outcome decided";
    if (status === "CLOSED") return t('label.complaint.step_closed') || "Closed";
    return status || "";
  };

  const handleUpdateActionStatus = () => {
    const formattedDueDate = selectedActionDueDate
      ? `${selectedActionDueDate.replace(/-/g, "/")}` + " 23:59"
      : null;

    dispatch(updateActionStatus({
      id: editingAction.id,
      status: selectedActionStatus,
      dueDate: formattedDueDate,
      file: editActionFile,
    })).then(() => {
      dispatch(fetchPageAction({ source: ACTION_SOURCE.COMPLAINT, complaintId: id, page: 1, size: 50 }));
    });
    setOpenEditActionDialog(false);
  };

  const getActionStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return { background: "#ECFDF5", color: "#047857" };
      case "IN_PROGRESS":
        return { background: "#FEF3C7", color: "#D97706" };
      case "OVERDUE":
        return { background: "#FEE2E2", color: "#B91C1C" };
      case "OPEN":
      default:
        return { background: "#EFF6FF", color: "#1D4ED8" };
    }
  };

  const getOwnerLabel = (owner) => {
    switch (owner) {
      case ACTION_OWNER.WORKER:
        return t('label.complaint.worker');
      case ACTION_OWNER.PARTICIPANT:
        return t('label.participant');
      case ACTION_OWNER.COMPANY:
        return t('label.company');
      default:
        return owner || "";
    }
  };

  const getActionStatusLabel = (status) => {
    switch (status) {
      case ACTION_STATUS.OPEN:
        return t('label.complaint.action_status_open');
      case ACTION_STATUS.IN_PROGRESS:
        return t('label.complaint.action_status_in_progress');
      case ACTION_STATUS.COMPLETED:
        return t('label.complaint.action_status_completed');
      case ACTION_STATUS.OVERDUE:
        return t('label.complaint.action_status_overdue');
      default:
        return status || "";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatInputDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleAddAction = (actionName, dueDate, owner, assignedToUserId, file) => {
    dispatch(createAction({
      source: ACTION_SOURCE.COMPLAINT,
      linkedRecordId: id,
      actionDescription: actionName,
      owner,
      assignedToUserId: (owner === ACTION_OWNER.WORKER || owner === ACTION_OWNER.COMPANY) ? assignedToUserId : null,
      dueDate: dueDate ? `${dueDate} 23:59` : null,
      file,
    })).then(() => {
      dispatch(fetchPageAction({ source: ACTION_SOURCE.COMPLAINT, complaintId: id, page: 1, size: 50 }));
    });
  };

  const handleFileUpload = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setIsUploading(true);
      dispatch(uploadComplaintEvidence({
        id,
        files: Array.from(files),
      })).finally(() => {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      });
    }
  };

  const handleDeleteEvidence = (evidenceId) => {
    setDeleteEvidenceTarget(evidenceId);
    setOpenDeleteEvidenceDialog(true);
  };

  const handleConfirmDeleteEvidence = () => {
    if (deleteEvidenceTarget) {
      dispatch(deleteComplaintEvidence({ id, evidenceId: deleteEvidenceTarget }));
    }
    setOpenDeleteEvidenceDialog(false);
    setDeleteEvidenceTarget(null);
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.breadcrumb}>
        <Box className={classes.breadcrumbLink} onClick={() => history.push("/admin/complaints")}>
          <i className="fas fa-arrow-left" style={{ fontSize: "13px" }} />
          {t('title.complaints')}
        </Box>
        <span style={{ color: "#aaa", fontSize: "13px" }}>/</span>
        <span style={{ fontSize: "13px", color: "#5f5e5a" }}>{getRefLabel()}</span>
      </Box>

      {complaint.slaOverdue && (
        <Box className={classes.slaBanner}>
          <i className="fas fa-clock" style={{ fontSize: "15px", color: "#A32D2D" }} />
          <span style={{ fontSize: "13px", fontWeight: 500, color: "#791F1F" }}>
            {t('label.complaint.sla_overdue_banner', { days: complaint.slaOverdueDays || 0 })}
          </span>
        </Box>
      )}

      <Box className={classes.headerRow}>
        <Box>
          <Typography className={classes.metaRef}>{getRefLabel()}</Typography>
          <Typography className={classes.headerTitle}>
            {complaint.complaintAbout || ""}
          </Typography>
          <Box className={classes.metaInfoRow}>
            <Typography className={classes.metaInfoItem}>
              <i className="fas fa-calendar" style={{ fontSize: "13px" }} />
              {t('label.complaint.received_on', { date: formatDate(complaint.createdAt) })}
            </Typography>
            <Typography className={classes.metaInfoItem}>
              <i className="fas fa-user" style={{ fontSize: "13px" }} />
              {getSubmitterLabel(complaint.complaintBy)}
            </Typography>
            {complaint.isAnonymous && (
              <Chip
                size="small"
                label={t('label.complaint.anonymous')}
                style={{ background: "#FCEBEB", color: "#A32D2D" }}
              />
            )}
          </Box>
        </Box>
        <Chip
          size="small"
          label={complaint.slaOverdue ? t('label.complaint.open_overdue') : complaint.status || ""}
          style={{
            background: complaint.slaOverdue ? "#FCEBEB" : "#E6F1FB",
            color: complaint.slaOverdue ? "#A32D2D" : "#185FA5",
            padding: "5px 14px",
            fontSize: "12px"
          }}
        />
      </Box>

      <Box className={classes.card}>
        <Box className={classes.cardTitle}>{t('label.complaint.resolution_steps')}</Box>
        <Box className={classes.stepsContainer}>
          {steps.map((step, index) => (
            <Box
              key={index}
              className={classes.stepItem}
              style={{
                cursor: index === nextStepIndex ? "pointer" : "default",
              }}
              onClick={() => handleStepClick(index)}
            >
              <Box
                className={classes.stepCircle}
                style={{
                  background: step.done
                    ? successColor
                    : step.active
                    ? "#fff"
                    : "#fff",
                  color: step.done
                    ? "#fff"
                    : step.active
                    ? successColor
                    : "#888780",
                  border: step.active ? `2px solid ${successColor}` : step.pending ? "2px solid #c5c4bc" : "none" }}
              >
                {step.done ? <i className="fas fa-check" style={{ fontSize: "14px" }} /> : index + 1}
              </Box>
              {index < steps.length - 1 && (
                <Box
                  className={classes.stepLine}
                  style={{
                    width: "50%",
                    background: step.done ? successColor : "#e5e5e2",
                    left: "50%",
                  }}
                />
              )}
              {index > 0 && (
                <Box
                  className={classes.stepLine}
                  style={{
                    width: "50%",
                    background: steps[index - 1].done ? successColor : "#e5e5e2",
                    left: "0%",
                  }}
                />
              )}
              <Typography
                className={classes.stepLabel}
                style={{
                  color: step.done ? successColor : step.active ? successColor : "#888780"
                }}
              >
                {step.label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box className={classes.stepHint}>
          {t('label.complaint.current_label')}{" "}
          <span style={{ color: successColor, fontWeight: 500 }}>{getCurrentStatusLabel()}</span>{" "}
          {complaint.optionOutcomeDecided && (
            <span style={{ fontWeight: 600, color: successColor }}>
              {" "}
              ({complaint.optionOutcomeDecided})
            </span>
          )}
          {nextStepIndex !== -1 && ` ${t('label.complaint.click_step_hint')}`}
          {(!complaint.status || complaint.status === "RECEIVED" || complaint.status === "UNDER_REVIEW") && (
            <span style={{ marginLeft: "8px", color: "#A32D2D" }}>
              {t('label.complaint.outcome_warning')}
            </span>
          )}
        </Box>
      </Box>

      <Box className={classes.card}>
        <Box className={classes.cardTitle}>
          <i className="fas fa-info-circle" style={{ color: successColor }} />
          {t('label.complaint.complaint_information')}
        </Box>
        <Box className={classes.infoGrid}>
          <Box>
            <Typography className={classes.infoLabel}>{t('label.complaint.submitted_by')}</Typography>
            <Typography className={classes.infoValue}>
              {getSubmitterLabel(complaint.complaintBy)} {complaint.isAnonymous ? t('label.complaint.anonymous_suffix') : ""}
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.infoLabel}>{t('label.complaint.relationship_to_care_aid')}</Typography>
            <Typography className={classes.infoValue}>{getRelationshipLabel(complaint.complaintRelationship)}</Typography>
          </Box>
          <Box>
            <Typography className={classes.infoLabel}>{t('label.complaint.assigned_to')}</Typography>
            <FormControl variant="outlined" size="small" style={{ minWidth: 200, marginTop: 4 }}>
              <Select
                value={complaint.assignedToUserId || ""}
                onChange={(e) => handleAssigneeChange(e.target.value)}
                displayEmpty
                style={{ fontSize: 13, height: 36, background: "white" }}
              >
                <MenuItem value="">
                  <em>{t('label.complaint.unassigned') || "Unassigned"}</em>
                </MenuItem>
                {companyUserOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box className={classes.infoItemFull}>
            <Typography className={classes.infoLabel}>{t('label.description')}</Typography>
            <Box className={classes.infoValueBox}>
              {complaint.complaintDescription || ""}
            </Box>
          </Box>
          {complaint.complaintDesiredOutcome && (
            <Box className={classes.infoItemFull}>
              <Typography className={classes.infoLabel}>{t('label.complaint.desired_outcome')}</Typography>
              <Box className={classes.infoValueBox}>
                {complaint.complaintDesiredOutcome}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Box className={classes.card}>
        <Box className={classes.cardTitle} style={{ justifyContent: "space-between" }}>
          <Box style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <i className="fas fa-list-check" style={{ color: successColor }} />
            {t('label.complaint.action_log')}
          </Box>
          <AppButton
            label={t('label.complaint.add_action')}
            buttonColor={successColor}
            rounder
            style={{ fontSize: "12px", padding: "6px 14px", height: "32px", minHeight: "32px" }}
            onClick={() => setIsModalOpen(true)}
          />
        </Box>
        <table className={classes.table}>
          <thead>
            <tr>
              <th style={{ width: "38%" }} className={classes.th}>{t('label.action')}</th>
              <th style={{ width: "20%" }} className={classes.th}>{t('label.complaint.owner')}</th>
              <th style={{ width: "22%" }} className={classes.th}>{t('label.complaint.due_date')}</th>
              <th style={{ width: "12%" }} className={classes.th}>{t('label.status')}</th>
              <th style={{ width: "8%" }} className={classes.th}></th>
            </tr>
          </thead>
          <tbody>
            {actions && actions.length > 0 ? (
              actions.map((action) => (
                <tr key={action.id}>
                  <td className={classes.td}>
                    <Box>
                      <Typography style={{ fontSize: "13px" }}>{action.actionDescription}</Typography>
                      {action.fileUrl && (
                        <Box style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "4px" }}>
                          <i className="fas fa-paperclip" style={{ fontSize: "11px", color: "#888780" }} />
                          <Typography style={{ fontSize: "11px", color: "#5f5e5a", marginRight: "4px" }}>
                            {action.fileName}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </td>
                  <td className={classes.td} style={{ color: "#5f5e5a" }}>{getActionAssignedName(action)}</td>
                  <td className={classes.td} style={{ color: action.status === "OVERDUE" ? "#A32D2D" : "#5f5e5a", fontWeight: 500 }}>
                    {formatDate(action.dueDate)}
                  </td>
                  <td className={classes.td}>
                    <Chip
                      size="small"
                      label={getActionStatusLabel(action.status)}
                      style={{
                        ...getActionStatusColor(action.status),
                        fontWeight: 500
                      }}
                    />
                  </td>
                  <td className={classes.td}>
                    <Box style={{ display: "flex", alignItems: "center", gap: "20px", justifyContent: "flex-end" }}>
                      {action.fileUrl && (
                        <>
                          <i
                            className="fas fa-eye"
                            title={t('button.view') || "View"}
                            style={{ fontSize: "13px", color: successColor, cursor: "pointer" }}
                            onClick={() => {
                              setPreviewFileUrl(action.fileUrl);
                              setOpenPreviewDialog(true);
                            }}
                          />
                          <a
                            href={action.fileUrl}
                            download={action.fileName}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none", color: "inherit", display: "inline-flex" }}
                          >
                            <i
                              className="fas fa-download"
                              title={t('label.complaint.download') || "Download"}
                              style={{ fontSize: "13px", color: successColor, cursor: "pointer" }}
                            />
                          </a>
                        </>
                      )}
                      <i
                        className="fas fa-pen"
                        title={t('label.edit') || "Edit"}
                        style={{ fontSize: "13px", color: successColor, cursor: "pointer" }}
                        onClick={() => {
                          setEditingAction(action);
                          setSelectedActionStatus(action.status);
                          setSelectedActionDueDate(formatInputDate(action.dueDate));
                          setEditActionFile(null);
                          setOpenEditActionDialog(true);
                        }}
                      />
                    </Box>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: "16px", color: "#888780" }}>
                  {t('label.complaint.no_actions_yet')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>

      <Box className={classes.card}>
        <Box className={classes.cardTitle}>
          <i className="fas fa-paperclip" style={{ color: successColor }} />
          {t('label.complaint.evidence_and_attachments')}
        </Box>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept=".pdf,.docx,.jpg,.jpeg,.png"
          multiple
          onChange={handleFileUpload}
        />
        <Box className={classes.uploadZone}>
          {isUploading ? (
            <CircularProgress size={24} style={{ color: successColor }} />
          ) : (
            <>
              <i className="fas fa-upload" style={{ fontSize: "24px", color: "#888780" }} />
              <Box style={{ fontSize: "13px", color: "#5f5e5a", marginTop: "6px" }}>
                {t('label.complaint.drag_and_drop')}{" "}
                <span
                  style={{ color: successColor, cursor: "pointer" }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t('label.complaint.browse')}
                </span>
              </Box>
              <Box style={{ fontSize: "11px", color: "#888780", marginTop: "2px" }}>
                {t('label.complaint.file_types_hint')}
              </Box>
            </>
          )}
        </Box>
        {evidenceList && evidenceList.length > 0 && (
          <Box style={{ marginTop: "16px" }}>
            {evidenceList.map((file) => (
              <Box
                key={file.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  borderBottom: "0.5px solid #e5e5e2",
                }}
              >
                <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <i className="fas fa-file" style={{ color: "#888780" }} />
                  <span style={{ fontSize: "13px" }}>{file.fileName}</span>
                </Box>
                <Box style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  {file.fileUrl && (
                    <>
                      <i
                        className="fas fa-eye"
                        title={t('button.view') || "View"}
                        style={{ fontSize: "14px", color: successColor, cursor: "pointer" }}
                        onClick={() => {
                          setPreviewFileUrl(file.fileUrl);
                          setOpenPreviewDialog(true);
                        }}
                      />
                      <a
                        href={file.fileUrl}
                        download={file.fileName}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "inline-flex", color: "inherit" }}
                      >
                        <i
                          className="fas fa-download"
                          title={t('label.complaint.download') || "Download"}
                          style={{ fontSize: "14px", color: successColor, cursor: "pointer" }}
                        />
                      </a>
                    </>
                  )}
                  <i
                    className="fas fa-trash"
                    style={{ fontSize: "14px", color: "#A32D2D", cursor: "pointer" }}
                    onClick={() => handleDeleteEvidence(file.id)}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box className={classes.footerButtons}>
        <AppButton
          label={t('label.complaint.export_to_pdf')}
          buttonColor="transparent"
          style={{ color: "#F59E0B", border: "1px solid #F59E0B", background: "white", fontSize: 11, padding: "4px 10px" }}
          startIcon={<i className="fas fa-file-export" />}
          rounder
        />
        <AppButton
          label={t('label.complaint.back_to_register')}
          buttonColor={AppColor.white}
          style={{
            fontSize: "13px",
            padding: "8px 16px",
            borderRadius: "8px",
            border: "0.5px solid #E5E5EB",
            color: "#6B7280",
          }}
          onClick={() => history.push("/admin/complaints")}
        />
      </Box>

      {/* Status Change Confirm Dialog */}
      <AppDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        title={t('label.complaint.confirm_status_title') || "Confirm Status Change"}
        maxWidth="sm"
        actions={[
          {
            label: t('button.cancel') || "Cancel",
            onPress: () => setOpenConfirmDialog(false),
            mode: "outlined",
          },
          {
            label: t('button.confirm') || "Confirm",
            onPress: handleConfirmStatusChange,
            buttonColor: successColor,
          }
        ]}
      >
        <Box>
          <Typography variant="body2" style={{ marginBottom: confirmStepIndex === 2 ? 16 : 0 }}>
            {t('label.complaint.confirm_status_msg', { status: steps[confirmStepIndex]?.label }) || 
              `Are you sure you want to transition this complaint to "${steps[confirmStepIndex]?.label}"?`}
          </Typography>
          
          {confirmStepIndex === 2 && (
            <FormControl variant="outlined" fullWidth size="small" style={{ marginTop: 12 }}>
              <Typography variant="subtitle2" style={{ marginBottom: 6, fontSize: 13, fontWeight: 500 }}>
                {t('label.complaint.select_outcome') || "Select Outcome Decision"}
              </Typography>
              <Select
                value={selectedOutcome}
                onChange={(e) => setSelectedOutcome(e.target.value)}
                style={{ fontSize: 13 }}
              >
                <MenuItem value="UPHELD">UPHELD</MenuItem>
                <MenuItem value="PARTIALLY_UPHELD">PARTIALLY_UPHELD</MenuItem>
                <MenuItem value="NOT_UPHELD">NOT_UPHELD</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      </AppDialog>

      {/* Edit Action Status Dialog */}
      <AppDialog
        open={openEditActionDialog}
        onClose={() => setOpenEditActionDialog(false)}
        title={t('label.complaint.update_action_status_title') || "Update Action Status"}
        maxWidth="sm"
        actions={[
          {
            label: t('button.cancel') || "Cancel",
            onPress: () => setOpenEditActionDialog(false),
            mode: "outlined",
          },
          {
            label: t('button.confirm') || "Confirm",
            onPress: handleUpdateActionStatus,
            buttonColor: successColor,
          }
        ]}
      >
        {editingAction && (
          <Box>
            <Typography variant="body2" style={{ marginBottom: 12, fontWeight: 500 }}>
              {t('label.complaint.action_name') || "Action Name"}: {editingAction.actionDescription}
            </Typography>
            
            <FormControl variant="outlined" fullWidth size="small" style={{ marginBottom: 16 }}>
              <Typography variant="subtitle2" style={{ marginBottom: 6, fontSize: 13, fontWeight: 500 }}>
                {t('label.status') || "Status"}
              </Typography>
              <Select
                value={selectedActionStatus}
                onChange={(e) => setSelectedActionStatus(e.target.value)}
                style={{ fontSize: 13 }}
              >
                <MenuItem value="OPEN">{t('label.complaint.action_status_open') || "Open"}</MenuItem>
                <MenuItem value="IN_PROGRESS">{t('label.complaint.action_status_in_progress') || "In progress"}</MenuItem>
                <MenuItem value="COMPLETED">{t('label.complaint.action_status_completed') || "Completed"}</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle2" style={{ marginBottom: 6, fontSize: 13, fontWeight: 500 }}>
              {t('label.complaint.due_date') || "Due Date"}
            </Typography>
            <input
              type="date"
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "4px",
                border: "1px solid #c5c4bc",
                fontSize: "13px",
                fontFamily: "inherit",
                marginBottom: "16px"
              }}
              value={selectedActionDueDate}
              onChange={(e) => setSelectedActionDueDate(e.target.value)}
            />

            <Typography variant="subtitle2" style={{ marginBottom: 6, fontSize: 13, fontWeight: 500 }}>
              {t('label.complaint.upload_file') || "Upload File"}
            </Typography>
            <Box style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "10px 14px",
              borderRadius: "4px",
              border: "1px solid #c5c4bc",
              backgroundColor: "#f9f9f6",
              marginBottom: "16px"
            }}>
              <i className="fas fa-file" style={{ fontSize: 18, color: "#6B7280" }} />
              <Box style={{ flex: 1, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {editActionFile ? editActionFile.name : (editingAction.fileName || <span style={{ color: "#888780" }}>{t('label.complaint.no_file_selected') || "No file selected"}</span>)}
              </Box>
              <AppButton
                label={editActionFile || editingAction.fileName ? t('label.complaint.change') || "Change" : t('label.complaint.upload') || "Upload"}
                mode="outlined"
                rounder
                style={{ fontSize: 11, textTransform: "none", height: 28, padding: "0 10px" }}
                onClick={() => {
                  if (editActionFileInputRef.current) {
                    editActionFileInputRef.current.value = null;
                    editActionFileInputRef.current.click();
                  }
                }}
              />
              <input
                accept="image/jpeg,image/png,image/jpg,application/pdf"
                style={{ display: 'none' }}
                type="file"
                ref={editActionFileInputRef}
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    setEditActionFile(files[0]);
                  }
                }}
              />
            </Box>
          </Box>
        )}
      </AppDialog>

      {/* Delete Evidence Confirm Dialog */}
      <AppDialog
        open={openDeleteEvidenceDialog}
        onClose={() => setOpenDeleteEvidenceDialog(false)}
        title={t('label.complaint.confirm_delete_evidence_title') || "Delete Evidence"}
        maxWidth="sm"
        actions={[
          {
            label: t('button.cancel') || "Cancel",
            onPress: () => setOpenDeleteEvidenceDialog(false),
            mode: "outlined",
          },
          {
            label: t('button.delete') || "Delete",
            onPress: handleConfirmDeleteEvidence,
            buttonColor: "#A32D2D",
          }
        ]}
      >
        <Typography variant="body2">
          {t('label.complaint.confirm_delete_evidence_msg') || "Are you sure you want to delete this file? This action cannot be undone."}
        </Typography>
      </AppDialog>

      <PreviewDialog
        open={openPreviewDialog}
        setOpen={setOpenPreviewDialog}
        fileUrl={previewFileUrl}
      />

      <ComplaintActionForm
        open={isModalOpen}
        companyUserOptions={companyUserOptions}
        workerOptions={workerOptions}
        onClose={() => setIsModalOpen(false)}
        onConfirm={(actionName, dueDate, owner, assignedToUserId, file) => {
          handleAddAction(actionName, dueDate, owner, assignedToUserId, file);
          setIsModalOpen(false);
        }}
      />
    </Box>
  );
};

export default ComplaintDetail;
